// Lightning — App shell: role bar, theme toggle, route switch, sidebar.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppRole, AppState, View, NavOpts } from './types';
import { GROUP_LETTERS } from './types';
import { createMockStore, createSupabaseStore, createPublicSnapshotStore, useAppState, useSystemStatus } from './store';
import type { AssembledState } from './store/assembleState';
import { OFFSEASON } from './config';
import seasonOneFinal from './data/season-1-final.json';
import { initialState } from './state/reducer';

// Frozen final-season public snapshot (captured from /api/public-state). Read by
// anonymous visitors in OFFSEASON mode so the archive needs no live DB.
const SEASON_1_FINAL = seasonOneFinal as unknown as AssembledState;
import { TEAMS } from './mock/data';
import { supabase } from './lib/supabase';
import { useAuth } from './lib/auth';
import { getMyHandle, setMyHandle } from './lib/fantasy-api';
import { ProtoSidebar, ProtoClockChip, StatusBanner, ErrorToast } from './components';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';

// View persistence: each role can land on a subset of views. After auth
// resolves we validate the restored view against the new role and redirect
// only if invalid (so reload preserves where the user was).
// Role-gated view sets. Exported for tests; used by the auth-resolve effect
// to redirect to a sensible default when a restored view is no longer
// allowed for the current role.
export const PUBLIC_VIEWS: View[] = [
  'sched-3v3', 'sched-1v1', 'sched-arctic', 'sched-ec', 'sched-practice', 'sponsors',
  'recap', 'standings', 'results', 'bracket', 'teams', 'pictures',
  // Fantasy is public-visible — anon users get a sign-in teaser; the real
  // predict/leaderboard experience needs auth (enforced in the screen + RLS).
  'fantasy',
];
// Volunteers get the same operational views as staff EXCEPT 'warnings' —
// they neither see nor issue warnings (the defining scoping rule for the
// tier). Server-side: warnings_insert RLS policies don't include volunteer.
export const VOLUNTEER_VIEWS: View[] = [
  ...PUBLIC_VIEWS, 'control', 'inspection', 'preinspection',
];
export const STAFF_VIEWS: View[] = [...VOLUNTEER_VIEWS, 'warnings'];
export const OPS_VIEWS: View[] = [...STAFF_VIEWS, 'checkin', 'fieldcal', 'referee', 'admin'];

// Offseason: the public archive drops the live schedule + sponsor surface. These
// views stay ROUTABLE (deep links, and signed-in roles still reach them for
// testing / Season Two) but are hidden from the public nav and disallowed as a
// public landing, so a returning visitor's stored schedule view redirects to the
// recap. The default public view becomes the Season One recap.
const OFFSEASON_HIDDEN_PUBLIC: View[] = ['sched-3v3', 'sched-1v1', 'sched-arctic', 'sched-ec', 'sched-practice', 'sponsors'];
export const PUBLIC_DEFAULT: View = OFFSEASON ? 'recap' : 'sched-3v3';

export function viewAllowedForRole(view: View, role: AppRole, isSuperuser = false): boolean {
  // Access Control is superuser-only (the three hardcoded admins, see auth.ts
  // SUPERUSER_EMAILS) — gated by identity rather than the role-based view sets.
  if (view === 'access') return isSuperuser;
  // Offseason hides the schedule/sponsor surface from PUBLIC only; signed-in
  // roles keep full access.
  if (OFFSEASON && role === 'public' && OFFSEASON_HIDDEN_PUBLIC.includes(view)) return false;
  const allowed =
    role === 'ops'       ? OPS_VIEWS :
    role === 'staff'     ? STAFF_VIEWS :
    role === 'volunteer' ? VOLUNTEER_VIEWS :
                           PUBLIC_VIEWS;
  return allowed.includes(view);
}

function restoreView(): View {
  try {
    const saved = localStorage.getItem('lightning.view');
    if (saved) return saved as View;
  } catch {
    // Storage unavailable (private mode, etc.) — fall back to default.
  }
  return 'control';
}

function restoreSelectedTeam(): string | null {
  try {
    return localStorage.getItem('lightning.selectedTeam') || null;
  } catch {
    return null;
  }
}

// Flip via VITE_USE_SUPABASE in .env.local. Default off so day-1 dev still
// works without seeded data.
const USE_SUPABASE = import.meta.env.VITE_USE_SUPABASE === 'true';

// E2E test seam — MockStore mode only. `?e2e_role=<role>` pins the viewing
// role so Playwright (and manual dev preview) can reach ops/staff/volunteer
// surfaces without real Google auth. Ignored entirely when Supabase is live:
// there the real JWT governs `role`, and server-side RLS is the actual
// security boundary, so this override never grants a real privilege.
function readE2ERoleOverride(): AppRole | null {
  if (USE_SUPABASE || typeof window === 'undefined') return null;
  try {
    const r = new URLSearchParams(window.location.search).get('e2e_role');
    if (r === 'public' || r === 'volunteer' || r === 'staff' || r === 'ops') return r;
  } catch {
    // Malformed URL — ignore and fall through to normal auth-driven role.
  }
  return null;
}
const E2E_ROLE = readE2ERoleOverride();

// E2E seams (MockStore mode only) — `?e2e_superuser=1` grants the Access Control
// superuser flag; `?e2e_authed=1` simulates a signed-in user for the auth-gated
// Fantasy surface. The real app derives both from the JWT.
const E2E_SUPERUSER = !USE_SUPABASE && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('e2e_superuser') === '1';
const E2E_AUTHED = !USE_SUPABASE && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('e2e_authed') === '1';

// E2E test seam — MockStore mode only. `?e2e_fixture=<name>` swaps the MockStore's
// seed state so tests can reach states the mid-event default snapshot can't —
// e.g. `pre-quals` (quals not begun) surfaces Match Control's practice panel and
// the Captain's Meeting seeding flow. Ignored when Supabase is live.
function readE2EFixture(): AppState | null {
  if (USE_SUPABASE || typeof window === 'undefined') return null;
  let fixture: string | null = null;
  try {
    fixture = new URLSearchParams(window.location.search).get('e2e_fixture');
  } catch {
    return null; // malformed URL — fall through to the default seed
  }

  if (fixture === 'pre-quals') {
    // The practice window: quals PUBLISHED (qualsLive) but not yet BEGUN
    // (qualsBegun false) — Match Control shows the practice panel and booking
    // is enabled. Two teams are pre-inspection-cleared for 3v3 so the booking
    // picker has options.
    return {
      ...initialState,
      qualsBegun: { '3v3': false, '1v1': false, 'ec': true },
      qualsLive:  { '3v3': true,  '1v1': true,  'ec': true },
      preInspectionLevel: {
        ...initialState.preInspectionLevel,
        VT: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
        UW: { '3v3': { Standard: 'l3', Hero: 'l3', Sentry: 'l3' } },
      },
    };
  }

  if (fixture === 'captains-meeting') {
    // Quals not yet published (qualsLive false, default), and every 3v3 team
    // round-robin-assigned across the four groups (≥2 per group) so the
    // seeding validates and Publish is enabled. All teams are marked
    // check-in-complete (the meeting runs after check-in), so the Captain's
    // Meeting check-in gate includes everyone.
    const teamConfig = { ...initialState.teamConfig };
    TEAMS.filter((t) => t.events.includes('3v3')).forEach((t, i) => {
      teamConfig[t.id] = { ...teamConfig[t.id], group: GROUP_LETTERS[i % GROUP_LETTERS.length] };
    });
    TEAMS.filter((t) => t.events.includes('1v1')).forEach((t, i) => {
      teamConfig[t.id] = { ...teamConfig[t.id], initialSeed1v1: i + 1 };
    });
    const checkIn = { ...initialState.checkIn };
    for (const t of TEAMS) {
      checkIn[t.id] = {
        spielGiven: true, badges: 5, badges1v1: true, badges3v3: true, badgesEc: true,
        borrowing: true, checkInForm: true, pointsPenalty3v3: 0, pointsPenaltyEc: 0, hasBackupRobot: false,
      };
    }
    return { ...initialState, teamConfig, checkIn };
  }

  if (fixture === 'delay-overflow') {
    // A large 3v3 delay anchored to the Day-1 Morning block (where the live
    // match sits) pushes the later morning matches past the 12:00 block end, so
    // they show OVERTIME + the push-to-next-block control.
    return {
      ...initialState,
      officialDelay:      { ...initialState.officialDelay, '3v3': 120 },
      officialDelayBlock: { ...initialState.officialDelayBlock, '3v3': 'D1-M' },
    };
  }

  if (fixture === 'swiss-done') {
    // Every 1v1 qual completed → the current Swiss round is done, so ops can
    // generate the next round's pairings.
    return {
      ...initialState,
      matches1v1: initialState.matches1v1.map((m) => ({
        ...m, redS: 'completed' as const, blueS: 'completed' as const, live: false, onDeck: false,
      })),
    };
  }

  return null;
}
const E2E_FIXTURE = readE2EFixture();

// PublicSchedules is the default landing for unauthenticated users (sched-3v3)
// and bundles 4 small siblings (Standings / ARCTIC / EC) — keep eager so the
// most common cold-start path is one fetch.
import {
  ProtoPublicSchedule, ProtoARCTIC, ProtoEC, ProtoStandings,
} from './screens/PublicSchedules';

// Everything else is route-split. Each screen ships in its own chunk and is
// fetched on first navigation (or earlier via the preload effect below).
// Public users never download MatchControl/Inspection/etc.; ops users only
// pay the fetch on the first deploy until the browser caches the chunk.
//
// The .then((m) => ({ default: ... })) shim is because the screens use named
// exports — React.lazy() wants a default export. Doing it here avoids
// touching every screen file.
const ProtoMatchControl  = lazy(() => import('./screens/MatchControl').then((m) => ({ default: m.ProtoMatchControl })));
const ProtoInspection    = lazy(() => import('./screens/Inspection').then((m) => ({ default: m.ProtoInspection })));
const ProtoWarnings      = lazy(() => import('./screens/Warnings').then((m) => ({ default: m.ProtoWarnings })));
const ProtoBracket       = lazy(() => import('./screens/Bracket').then((m) => ({ default: m.ProtoBracket })));
const ProtoFantasyBracket = lazy(() => import('./screens/FantasyBracket').then((m) => ({ default: m.ProtoFantasyBracket })));
const ProtoTeams         = lazy(() => import('./screens/Teams').then((m) => ({ default: m.ProtoTeams })));
const ProtoTeamPictures  = lazy(() => import('./screens/TeamPictures').then((m) => ({ default: m.ProtoTeamPictures })));
const ProtoSponsors      = lazy(() => import('./screens/Sponsors').then((m) => ({ default: m.ProtoSponsors })));
const ProtoPreInspection = lazy(() => import('./screens/PreInspection').then((m) => ({ default: m.ProtoPreInspection })));
const ProtoCheckIn       = lazy(() => import('./screens/CheckIn').then((m) => ({ default: m.ProtoCheckIn })));
const ProtoFieldCalibration = lazy(() => import('./screens/FieldCalibration').then((m) => ({ default: m.ProtoFieldCalibration })));
const ProtoReferee = lazy(() => import('./screens/Referee').then((m) => ({ default: m.ProtoReferee })));
const ProtoResults       = lazy(() => import('./screens/Results').then((m) => ({ default: m.ProtoResults })));
const ProtoAdmin         = lazy(() => import('./screens/Admin').then((m) => ({ default: m.ProtoAdmin })));
const ProtoAccessControl = lazy(() => import('./screens/AccessControl').then((m) => ({ default: m.ProtoAccessControl })));
const ProtoPracticeSchedule = lazy(() => import('./screens/PracticeSchedule').then((m) => ({ default: m.ProtoPracticeSchedule })));
const ProtoRecap         = lazy(() => import('./screens/Recap').then((m) => ({ default: m.ProtoRecap })));

// Suspense fallback while a lazy chunk is fetching. Subtle to avoid flashing
// — most chunks resolve in <100ms on broadband and are cached after first load.
function RouteFallback() {
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-3)' }}>
      <span className="mono" style={{ fontSize: 11, letterSpacing: '0.1em' }}>LOADING…</span>
    </div>
  );
}

function AuthChip({ auth }: { auth: ReturnType<typeof useAuth> }) {
  if (auth.status === 'loading') {
    return (
      <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', padding: '0 8px' }}>…</span>
    );
  }
  if (auth.status === 'anonymous') {
    return (
      <button
        className="btn"
        onClick={() => auth.signIn()}
        title="Sign in with Google (arc-robotics.org or an allow-listed personal account)"
        style={{ padding: '6px 10px', fontSize: 11 }}
      >
        <span className="mono">SIGN IN</span>
      </button>
    );
  }
  // authenticated
  const initials = deriveInitials(auth.email ?? '');
  const roleLabel = auth.role ? auth.role.toUpperCase() : 'NO ROLE';
  const tooltip = auth.role
    ? `${auth.email} · ${roleLabel}`
    : `${auth.email}\n\nNo app_role claim on the JWT. Either the auth hook isn't registered (Supabase → Auth → Hooks → Customize Access Token (JWT) Claims Hook → public.custom_access_token_hook) or your email isn't synced into ops_emails (run npm run sync:ops).`;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 8px', borderRadius: 6,
        background: 'var(--bg-2)',
        border: 'var(--bw) solid ' + (auth.role ? 'var(--line)' : 'rgba(255,176,32,0.5)'),
      }}
      title={tooltip}
    >
      <div style={{
        width: 22, height: 22, borderRadius: 999, background: 'var(--bg-3)',
        display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 9,
        letterSpacing: '0.04em',
      }}>{initials}</div>
      <span className="mono" style={{ fontSize: 10, color: auth.role ? 'var(--text-1)' : 'var(--warn)', letterSpacing: '0.06em' }}>
        {roleLabel}
      </span>
      <button
        className="btn ghost"
        onClick={() => auth.signOut()}
        style={{ padding: '2px 6px', fontSize: 9, marginLeft: 2 }}
        title="Sign out"
      >
        <span className="mono">⏻</span>
      </button>
    </div>
  );
}

// "tudor.fanaru@arc-robotics.org" → "Tudor Fanaru"
function deriveName(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter((p) => p.length > 0);
  if (!parts.length) return email;
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

// "tudor.fanaru@…" → "TF"; "td@…" → "TD"; "headref-3v3@…" → "H3".
function deriveInitials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter((p) => p.length > 0);
  if (parts.length >= 2) return (parts[0][0]! + parts[1][0]!).toUpperCase();
  return local.slice(0, 2).toUpperCase() || '??';
}

function Stub({ label }: { label: string }) {
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-2)' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="display" style={{ fontSize: 32, color: 'var(--text-1)' }}>{label}</div>
        <div className="mono" style={{ fontSize: 11, marginTop: 8 }}>STUB · NOT YET DESIGNED</div>
      </div>
    </div>
  );
}

// Edit-your-fantasy-display-name modal, opened from the sidebar user footer.
function FantasyNameModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { void getMyHandle().then((h) => setName(h ?? '')); }, []);
  const save = async () => {
    try { await setMyHandle(name); onClose(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} className="card col gap-3" style={{ padding: 18, width: 320 }}>
        <div className="card-title">DISPLAY NAME</div>
        <div className="mono txt-10 text-3">Shown on the fantasy leaderboard (not your email).</div>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
          className="txt-12 bg-2 border r-5 text-0" style={{ padding: '8px 10px' }} />
        {err && <div className="mono txt-10 text-err">{err}</div>}
        <div className="row gap-2 end">
          <button className="btn ghost txt-11" onClick={onClose} style={{ padding: '7px 12px' }}><span className="mono">CANCEL</span></button>
          <button className="btn primary txt-11" onClick={save} disabled={!name.trim()} style={{ padding: '7px 12px', opacity: name.trim() ? 1 : 0.5 }}><span className="mono">SAVE</span></button>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  // The role tabs in the bar are an explicit "view as" override — useful for
  // dev/testing and for ops to verify staff/public views. The default role
  // tracks the authenticated session's app_role (or 'public' when signed out).
  const [role, setRole] = useState<AppRole>(E2E_ROLE ?? 'public');
  // View + selectedTeam are persisted to localStorage so reloads land you back
  // where you were. Initial restore happens here; the auth-resolve effect
  // below validates the restored view against the new role.
  const [view, setView] = useState<View>(restoreView);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(restoreSelectedTeam);
  // When the user clicks a team chip from a non-Teams view (Standings,
  // Schedule, Results, etc.), remember where they came from so the team
  // detail's back arrow returns them there instead of dumping them on the
  // Teams directory. Cleared once the user navigates back or to anywhere
  // other than a team detail.
  const [teamReturnView, setTeamReturnView] = useState<View | null>(null);
  // Sidebar visibility — persisted to localStorage so toggling it survives
  // navigation. Default: open on desktop, closed on mobile (matchMedia at
  // mount). The hamburger button toggles state on either breakpoint:
  //   - Desktop: collapse the sidebar out of the flex flow.
  //   - Mobile: slide the drawer in/out.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('lightning.sidebarOpen');
      if (saved === 'true') return true;
      if (saved === 'false') return false;
    } catch {
      // Storage unavailable (private mode, etc.) — fall through to default.
    }
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 761px)').matches;
  });
  const [editNameOpen, setEditNameOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('lightning.sidebarOpen', String(sidebarOpen)); } catch { /* ignore */ }
  }, [sidebarOpen]);
  const auth = useAuth();
  // Access Control superuser + the two delegated permission grants. E2E pins
  // them via query params (MockStore); the real app reads JWT claims.
  const isSuperuser = E2E_ROLE ? E2E_SUPERUSER : auth.isSuperuser;
  const grantL3 = E2E_ROLE ? false : auth.l3Override;
  const grantCaptains = E2E_ROLE ? false : auth.captainsMeeting;
  const initialAuthSettledRef = useRef(false);
  // Latest view, readable from effects without putting view in their deps.
  const viewRef = useRef(view);
  viewRef.current = view;

  // OAuth callback cleanup. The Supabase SDK (with detectSessionInUrl: true
  // + flowType: 'pkce') parses the `?code=...` query param on mount and
  // exchanges it for tokens. Belt-and-suspenders: once the SDK has had a
  // tick to consume it, strip any auth-callback params or implicit-flow
  // fragment so they don't linger in browser history or get copy-pasted.
  // Token values never appear in URL with PKCE — this is just paranoia
  // against the implicit-flow #access_token=… fragment showing up if a
  // legacy redirect hits the page before the new flow takes effect.
  useEffect(() => {
    const hasAuthCallback =
      window.location.hash.includes('access_token=') ||
      window.location.hash.includes('refresh_token=') ||
      window.location.search.includes('code=');
    if (!hasAuthCallback) return;
    const t = window.setTimeout(() => {
      window.history.replaceState(null, '', window.location.pathname);
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  // Persist view + selectedTeam.
  useEffect(() => {
    try { localStorage.setItem('lightning.view', view); } catch { /* ignore */ }
  }, [view]);
  useEffect(() => {
    try {
      if (selectedTeam) localStorage.setItem('lightning.selectedTeam', selectedTeam);
      else localStorage.removeItem('lightning.selectedTeam');
    } catch { /* ignore */ }
  }, [selectedTeam]);
  // Phase 1.5+: state is owned by a persistence-agnostic Store. The flag
  // chooses between in-memory mock and Supabase reads (with realtime). Phase
  // 3 part 2 will wire dispatch through the DB; until then SupabaseStore
  // mutates locally same as Mock.
  // Wrapped in try/catch so a SupabaseStore init error falls back to Mock with
  // a console diagnostic, rather than black-screening the whole app.
  const [storeError, setStoreError] = useState<string | null>(null);
  // Spectators (anyone not vol+) read the edge-cached /api/public-state snapshot
  // — no realtime channels, no per-edit refetch — so 100s of viewers never touch
  // the DB connection pool. vol+ get the realtime read/write SupabaseStore.
  const isVolPlus = auth.status === 'authenticated'
    && (auth.role === 'volunteer' || auth.role === 'staff' || auth.role === 'ops');
  // Any signed-in user (incl. roleless) — gates the Fantasy surface. E2E mock
  // mode simulates it via ?e2e_authed=1 or any non-public pinned role.
  const isAuthenticated = auth.status === 'authenticated' || E2E_AUTHED
    || (E2E_ROLE !== null && E2E_ROLE !== 'public');
  const store = useMemo(() => {
    if (!USE_SUPABASE) return createMockStore(E2E_FIXTURE ?? undefined);
    // Offseason: anon reads the committed frozen snapshot (no live polling).
    // In-season: anon polls the edge-cached /api/public-state.
    if (!isVolPlus) return createPublicSnapshotStore(OFFSEASON ? { frozen: SEASON_1_FINAL } : {});
    try {
      return createSupabaseStore(supabase);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error('SupabaseStore init failed, falling back to MockStore:', err);
      setStoreError(msg);
      return createMockStore();
    }
  }, [isVolPlus]);
  // Tear down the PREVIOUS store (stop its polling) when the store is swapped —
  // e.g. a spectator signs in as vol+ and the snapshot store is replaced. Done
  // via a ref rather than effect-cleanup so React 18 StrictMode's double-invoke
  // can't dispose the live store.
  const prevStore = useRef(store);
  useEffect(() => {
    if (prevStore.current !== store) {
      prevStore.current.dispose?.();
      prevStore.current = store;
    }
  }, [store]);
  const [state, dispatch] = useAppState(store);
  const sys = useSystemStatus(store);
  const dismissError = useCallback(() => store.clearLastError(), [store]);
  // One-time visibility log so devs can confirm in console which backend is live.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info(`%c Lightning %c ${store.kind.toUpperCase()} `,
      'background:#FFD60A;color:#0A0A0F;font-weight:700;padding:2px 6px;border-radius:3px 0 0 3px',
      'background:#3DDC84;color:#0A0A0F;font-weight:700;padding:2px 6px;border-radius:0 3px 3px 0');
  }, [store.kind]);

  // Auth → role. On INITIAL resolution, respect the localStorage-restored
  // view if it's valid for the new role (so reload doesn't jump pages). On
  // SUBSEQUENT auth changes (sign-in/out mid-session), navigate to a sensible
  // default for the new role.
  useEffect(() => {
    if (auth.status === 'loading') return;
    // E2E override pins the role (MockStore mode); don't let auth resolution
    // clobber it. Still honor the restored-view validation on first settle.
    if (E2E_ROLE) {
      if (!initialAuthSettledRef.current) {
        initialAuthSettledRef.current = true;
        if (!viewAllowedForRole(viewRef.current, E2E_ROLE, isSuperuser)) {
          setView(E2E_ROLE === 'public' ? PUBLIC_DEFAULT : 'control');
        }
      }
      return;
    }
    const next = auth.status === 'authenticated' ? (auth.role ?? 'public') : 'public';
    setRole(next);
    const defaultView: View = next === 'public' ? PUBLIC_DEFAULT : 'control';
    if (!initialAuthSettledRef.current) {
      initialAuthSettledRef.current = true;
      if (!viewAllowedForRole(viewRef.current, next, isSuperuser)) setView(defaultView);
      return;
    }
    setView(defaultView);
  }, [auth.status, auth.role, isSuperuser]);

  // Preload route chunks based on role. Browser fetches them in the background
  // while the user is on the landing screen, so the next click feels instant.
  // Public users never preload the auth-only chunks. Idempotent: each import()
  // is module-cache deduped, so repeat calls are free.
  useEffect(() => {
    if (role === 'volunteer' || role === 'staff' || role === 'ops') {
      // Highest priority: the default landing (Match Control) and the screens
      // staff/ops/volunteer use mid-event.
      void import('./screens/MatchControl');
      void import('./screens/Inspection');
      void import('./screens/PreInspection');
      void import('./screens/Teams');
      void import('./screens/Bracket');
      // Warnings: staff and ops only — volunteers don't have access.
      if (role === 'staff' || role === 'ops') void import('./screens/Warnings');
      if (role === 'ops') void import('./screens/CheckIn'); // ops-only edits
      if (role === 'ops') void import('./screens/FieldCalibration'); // ops-only
      if (role === 'ops') void import('./screens/Referee'); // ops-only
      if (role === 'ops') void import('./screens/Admin');   // ops-only pre-event seeding
    } else {
      // Public — kick off the secondary public screens (Standings / Schedules
      // are already eager via PublicSchedules). Bracket + Teams are the
      // most-clicked next stops.
      void import('./screens/Bracket');
      void import('./screens/Teams');
      void import('./screens/TeamPictures');
    }
  }, [role]);

  const navigate = (v: View, opts?: NavOpts) => {
    // Team-detail back-navigation: if the user is leaving a team detail
    // via the back arrow (team === '__clear__') and we recorded a return
    // view earlier, restore that view instead of dumping them on the
    // Teams directory.
    if (opts && opts.team === '__clear__' && teamReturnView) {
      setSelectedTeam(null);
      setView(teamReturnView);
      setTeamReturnView(null);
      return;
    }
    if (opts && opts.team !== undefined) {
      // Entering a team detail from a non-teams view → remember the
      // current view so the back arrow can return to it.
      if (opts.team !== '__clear__' && view !== 'teams') {
        setTeamReturnView(view);
      }
      // Manual navigation away from a team detail (e.g., sidebar click)
      // invalidates the captured return view — the user has chosen a
      // different destination.
      if (opts.team === '__clear__') setTeamReturnView(null);
      setSelectedTeam(opts.team === '__clear__' ? null : opts.team);
    }
    if (opts && opts.warningPrefill) dispatch({ type: 'SET_PENDING_WARNING', prefill: opts.warningPrefill });
    setView(v);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const switchRole = (r: AppRole) => {
    setRole(r);
    // Preserve the current view if the new role still has access. Only
    // redirect when the current view becomes inaccessible (e.g. switching
    // from ops/staff to public while on Match Control). Picks a sensible
    // default per role: public → public schedule; staff/ops → match control.
    if (!viewAllowedForRole(view, r, isSuperuser)) {
      setView(r === 'public' ? PUBLIC_DEFAULT : 'control');
    }
  };

  const user = auth.status === 'authenticated' && auth.email
    ? { name: deriveName(auth.email), initials: deriveInitials(auth.email) }
    : { name: 'Spectator', initials: 'SP' };

  // Which role tabs to allow clicking. Public is always allowed; higher
  // tiers require an authenticated user with at-least that JWT claim.
  // Tier hierarchy: public < volunteer < staff < ops.
  const tabAllowed: Record<AppRole, boolean> = {
    public:    true,
    volunteer: auth.status === 'authenticated'
               && (auth.role === 'volunteer' || auth.role === 'staff' || auth.role === 'ops'),
    staff:     auth.status === 'authenticated' && (auth.role === 'staff' || auth.role === 'ops'),
    ops:       auth.role === 'ops',
  };
  // Trim the "Viewing as" menu to options the user can actually pick.
  // Anon: hide picker entirely. Each tier sees their own + lower tiers.
  const visibleRoleTabs: { id: AppRole; label: string; lock?: boolean }[] = (() => {
    if (auth.role === 'ops') {
      return [
        { id: 'public',    label: 'PUBLIC' },
        { id: 'volunteer', label: 'VOLUNTEER' },
        { id: 'staff',     label: 'STAFF' },
        { id: 'ops',       label: 'OPERATIONS', lock: true },
      ];
    }
    if (auth.role === 'staff') {
      return [
        { id: 'public',    label: 'PUBLIC' },
        { id: 'volunteer', label: 'VOLUNTEER' },
        { id: 'staff',     label: 'STAFF' },
      ];
    }
    if (auth.role === 'volunteer') {
      return [
        { id: 'public',    label: 'PUBLIC' },
        { id: 'volunteer', label: 'VOLUNTEER' },
      ];
    }
    return []; // anon — no viewing-as picker at all
  })();

  // The mobile public screens render their own MobileTopbar with a
  // hamburger button — that hamburger needs to toggle the same sidebar
  // drawer state the desktop role-bar hamburger uses. Pass through as
  // `onMenu` so the mobile Topbar can fire it directly.
  const openMenu = () => setSidebarOpen(true);
  const renderView = () => {
    switch (view) {
      case 'sched-3v3':    return <ProtoPublicSchedule event="3v3" state={state} onNavigate={navigate} onMenu={openMenu} role={role} dispatch={dispatch} />;
      case 'sched-1v1':    return <ProtoPublicSchedule event="1v1" state={state} onNavigate={navigate} onMenu={openMenu} role={role} dispatch={dispatch} />;
      case 'sched-arctic': return <ProtoARCTIC state={state} onMenu={openMenu} onNavigate={navigate} />;
      case 'sched-ec':     return <ProtoEC state={state} onMenu={openMenu} onNavigate={navigate} role={role} dispatch={dispatch} />;
      case 'sched-practice': return <ProtoPracticeSchedule state={state} onNavigate={navigate} onMenu={openMenu} />;
      case 'sponsors':       return <ProtoSponsors />;
      case 'recap':        return <ProtoRecap onNavigate={navigate} />;
      case 'standings':    return <ProtoStandings state={state} onNavigate={navigate} onMenu={openMenu} />;
      case 'results':      return <ProtoResults state={state} dispatch={dispatch} role={role} onNavigate={navigate} onMenu={openMenu} />;
      case 'bracket':      return <ProtoBracket state={state} onNavigate={navigate} />;
      case 'control':
        return role === 'public'
          ? <ProtoPublicSchedule event="3v3" state={state} onNavigate={navigate} onMenu={openMenu} role={role} dispatch={dispatch} />
          : <ProtoMatchControl state={state} dispatch={dispatch} role={role} onNavigate={navigate} canRunCaptainsMeeting={role === 'ops' || grantCaptains} />;
      case 'inspection':    return <ProtoInspection state={state} dispatch={dispatch} role={role} userId={auth.user?.id ?? null} onNavigate={navigate} canL3Override={role === 'ops' || grantL3} />;
      case 'preinspection': return <ProtoPreInspection state={state} dispatch={dispatch} role={role} />;
      case 'checkin':       return <ProtoCheckIn state={state} dispatch={dispatch} role={role} />;
      case 'fieldcal':      return <ProtoFieldCalibration state={state} dispatch={dispatch} role={role} />;
      case 'referee':       return <ProtoReferee state={state} dispatch={dispatch} role={role} />;
      case 'warnings':      return <ProtoWarnings state={state} dispatch={dispatch} role={role} onNavigate={navigate} userEmail={auth.email ?? null} />;
      case 'teams':         return <ProtoTeams state={state} dispatch={dispatch} role={role} onNavigate={navigate} selectedTeam={selectedTeam} onMenu={openMenu} />;
      case 'pictures':      return <ProtoTeamPictures state={state} dispatch={dispatch} role={role} />;
      case 'admin':         return <ProtoAdmin state={state} dispatch={dispatch} role={role} onNavigate={navigate} canRunCaptainsMeeting={role === 'ops' || grantCaptains} />;
      case 'access':        return isSuperuser ? <ProtoAccessControl state={state} dispatch={dispatch} /> : <Stub label="Access Control" />;
      case 'fantasy':       return <ProtoFantasyBracket state={state} isAuthenticated={isAuthenticated} onSignIn={auth.signIn} />;
      default:               return <ProtoPublicSchedule event="3v3" state={state} onNavigate={navigate} onMenu={openMenu} role={role} dispatch={dispatch} />;
    }
  };

  return (
    <>
      {storeError && (
        <StatusBanner tone="error">
          ⚠ SupabaseStore failed to init — falling back to MockStore. {storeError}
        </StatusBanner>
      )}
      {sys.schemaVersionMismatch && (
        <StatusBanner tone="error">
          ⚠ App version mismatch — client v{sys.schemaVersionMismatch.client}, server v{sys.schemaVersionMismatch.server}.
          {' '}Reload to update.
        </StatusBanner>
      )}
      {sys.realtime === 'degraded' && (
        <StatusBanner tone="warn">
          ⚠ Live updates interrupted — showing last-known data. Reconnecting…{' '}
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'transparent', border: 'none', color: 'inherit',
              font: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0,
            }}
          >
            Refresh
          </button>{' '}
          if it doesn’t clear shortly.
        </StatusBanner>
      )}
      {sys.lastError && (
        <ErrorToast
          message={`${sys.lastError.actionType} failed: ${sys.lastError.message}`}
          onDismiss={dismissError}
        />
      )}
      <div className="app-frame">
      <ProtoSidebar
        role={role}
        active={view}
        onNavigate={(v) => setView(v)}
        user={user}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        state={state}
        isSuperuser={isSuperuser}
        isAuthenticated={isAuthenticated}
        onEditName={() => setEditNameOpen(true)}
      />
      {editNameOpen && <FantasyNameModal onClose={() => setEditNameOpen(false)} />}
      <div className="app-main">
        <div className="role-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              className="icon-btn"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              onClick={() => setSidebarOpen((s) => !s)}
              style={{ marginRight: 4 }}
            >☰</div>
            {visibleRoleTabs.length > 0 && (
              <>
                <span className="mono role-bar-label" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em' }}>VIEWING AS</span>
                <div className="role-tabs">
                  {visibleRoleTabs.map((r) => {
                    const allowed = tabAllowed[r.id];
                    return (
                      <div
                        key={r.id}
                        className={`role-tab ${role === r.id ? 'active' : ''}`}
                        onClick={() => allowed && switchRole(r.id)}
                        title={`View as ${r.label.toLowerCase()}`}
                        style={allowed ? undefined : { opacity: 0.35, cursor: 'not-allowed' }}
                      >
                        {r.label}{r.lock && <span className="lock">⚿</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {auth.status === 'authenticated' && (
              <span className="mono role-bar-desc" style={{ fontSize: 10, color: 'var(--text-3)' }}>
                {auth.role === 'ops'       ? 'GOOGLE SSO · FULL CONTROL'
                  : auth.role === 'staff'     ? 'GOOGLE SSO · WRITE ACCESS'
                  : auth.role === 'volunteer' ? 'GOOGLE SSO · INSPECTION · NO WARNINGS'
                  : 'AUTH HOOK PENDING · NO WRITES'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* App-level clock: live Eastern time; the dot reflects the data source. */}
            <ProtoClockChip status={store.kind} />
            <AuthChip auth={auth} />
            <div className="icon-btn" title="Toggle theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? '☾' : '☀'}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <RouteErrorBoundary key={view} loadingFallback={<RouteFallback />}>
            <Suspense fallback={<RouteFallback />}>
              {renderView()}
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </div>
      </div>
    </>
  );
}
